import type {
	AdminQuerySecodaDocumentsVariables,
	RegenerateTeamsPermissionsIn,
} from '@repo/api-codegen';
import {
	fetchAdminDownloadS3File,
	fetchAdminQuerySecodaDocuments,
	fetchClearQueryParseResults,
	fetchDeleteFromCache,
	fetchInspectCache,
} from '@repo/api-codegen';
import type {
	GenerateWorkspaceLineageParams,
	IBulkExportParams,
	IFetchLineageParams,
	IFetchResourceParams,
	IFetchWorkspaceParams,
	IRemoveDisabledUsersFromTeamsParams,
	IResetWorkspaceDqs2PoliciesParams,
	ITestConnectivityParams,
	ITestTunnelConnectivityParams,
	IUpdateWorkspaceQualityFlagsParams,
} from './actions';
import {
	bulkExport,
	fetchLineage,
	fetchLineageCount,
	fetchResource,
	fetchResourceCount,
	fetchWorkspace,
	generateWorkspaceLineage,
	regenerateTeamsPermissions,
	regenerateWorkspacePolicies,
	removeDisabledUsersFromTeams,
	resetWorkspaceDqs2Policies,
	testConnectivity,
	testTunnelConnectivity,
	updateAllowedDomains,
	updateDisabled,
	updateWorkspaceQualityFlags,
} from './actions';
import { ACTION_FIELDS } from './constants';
import { ACTION_TYPE } from './types';

export const getInitialFormValues = (type: ACTION_TYPE) => {
	const initialValues: Record<string, string | boolean> = {};
	ACTION_FIELDS[type].forEach((field) => {
		initialValues[field.value] = field.type === 'string' ? '' : false;
	});
	return initialValues;
};

export const onQueryAction = (
	type: ACTION_TYPE,
	params: Record<string, string | boolean>
) => {
	switch (type) {
		case ACTION_TYPE.GET_LINEAGE:
			return fetchLineage(params as IFetchLineageParams);
		case ACTION_TYPE.GET_LINEAGE_COUNT:
			return fetchLineageCount(params as IFetchLineageParams);
		case ACTION_TYPE.GET_RESOURCE:
			return fetchResource(params as IFetchResourceParams);
		case ACTION_TYPE.GET_WORKSPACE:
			return fetchWorkspace(params as IFetchWorkspaceParams);
		case ACTION_TYPE.UPDATE_ALLOWED_DOMAINS:
			return updateAllowedDomains({
				workspace_id: params.workspace_id as string,
				allowed_domains: params.allowed_domains as string,
				totp: params.totp as string,
			});
		case ACTION_TYPE.UPDATE_DISABLED:
			return updateDisabled({
				workspace_id: params.workspace_id as string,
				disabled: params.disabled as boolean,
				totp: params.totp as string,
			});
		case ACTION_TYPE.GET_RESOURCE_COUNT:
			return fetchResourceCount(params as IFetchResourceParams);
		case ACTION_TYPE.QUERY_ELASTICSEARCH:
			if (params.catalog_filter) {
				const catalogFilter = JSON.parse(params.catalog_filter as string);
				return fetchAdminQuerySecodaDocuments({
					body: {
						catalog_filter: catalogFilter,
						start: Number(params.start),
						end: Number(params.end),
						direct_es_query: params.direct_es_query as boolean,
						index:
							params.index as AdminQuerySecodaDocumentsVariables['body']['index'],
					},
				});
			}
			throw Error('Missing or invalid catalog filter');
		case ACTION_TYPE.DOWNLOAD_S3_FILE:
			return fetchAdminDownloadS3File({
				queryParams: {
					file_path: params.file_path as string,
					bucket: params.bucket as string,
				},
			});
		case ACTION_TYPE.INSPECT_CACHE:
			return fetchInspectCache({
				pathParams: {
					cacheKey: params.cache_key as string,
				},
			});
		case ACTION_TYPE.DELETE_FROM_CACHE:
			return fetchDeleteFromCache({
				pathParams: {
					cacheKey: params.cache_key as string,
				},
			});
		case ACTION_TYPE.CLEAR_QUERY_PARSE_RESULTS:
			return fetchClearQueryParseResults({
				pathParams: {
					integrationId: params.integration_id as string,
				},
				queryParams: {
					...(params.databuilder_prefix && {
						databuilder_prefix: params.databuilder_prefix as string,
					}),
				},
			});
		case ACTION_TYPE.REGENERATE_WORKSPACE_POLICIES:
			return regenerateWorkspacePolicies({
				workspace_id: params.workspace_id as string,
				complete_rebuild: params.complete_rebuild as boolean,
			});
		case ACTION_TYPE.UPDATE_WORKSPACE_QUALITY_FLAGS:
			return updateWorkspaceQualityFlags({
				workspace_id: params.workspace_id as string,
			} as IUpdateWorkspaceQualityFlagsParams);
		case ACTION_TYPE.RESET_DQS2_POLICIES:
			return resetWorkspaceDqs2Policies({
				workspace_id: params.workspace_id as string,
				totp: params.totp as string,
			} as IResetWorkspaceDqs2PoliciesParams);
		case ACTION_TYPE.TEST_REVERSE_TUNNEL:
			return testTunnelConnectivity({
				ssh_tunnel_id: params.ssh_tunnel_id as string,
				target_hostname: params.target_hostname as string,
				target_port: Number(params.target_port),
				timeout_seconds: Number(params.timeout_seconds) || 10,
				totp: params.totp as string,
			} as ITestTunnelConnectivityParams);
		case ACTION_TYPE.TEST_CONNECTIVITY:
			return testConnectivity({
				integration_id: params.integration_id as string,
				override_json: params.override_json as string,
				superuser_totp: params.superuser_totp as string,
			} as ITestConnectivityParams);
		case ACTION_TYPE.BULK_EXPORT:
			return bulkExport({
				workspace_id: params.workspace_id as string,
				entity_type: params.entity_type as string,
				...(params.email ? { email: params.email as string } : {}),
				totp: params.totp as string,
			} as IBulkExportParams);
		case ACTION_TYPE.REGENERATE_TEAMS_PERMISSIONS:
			return regenerateTeamsPermissions({
				workspace_id: params.workspace_id as string,
				integration_id: params.integration_id as string,
				create_ancestry: params.create_ancestry as boolean,
			} as RegenerateTeamsPermissionsIn);
		case ACTION_TYPE.GENERATE_WORKSPACE_LINEAGE:
			return generateWorkspaceLineage({
				workspace_id: params.workspace_id as string,
			} as GenerateWorkspaceLineageParams);
		case ACTION_TYPE.REMOVE_DISABLED_USERS_FROM_TEAMS:
			return removeDisabledUsersFromTeams({
				workspace_id: params.workspace_id as string,
			} as IRemoveDisabledUsersFromTeamsParams);
		default:
			throw new Error(`Unsupported action type: ${type}`);
	}
};
